import React from 'react';
import cn from 'classnames';

import { mq } from 'react-scale/components/website/big/Themes';

const Grid = ({ children, tag, rowGap, margin, vAlign }) => {
  const CustomTag = tag;
  const hydratedRowGap = { ...Grid.defaultProps.rowGap, ...rowGap };
  const hydratedMargin = { ...Grid.defaultProps.margin, ...margin };
  return (
    <CustomTag className="grid">
      {children}
      <style jsx>{`
        .grid {
          margin: 0 0 calc(8px * ${hydratedMargin.xs});
          padding: 0;
          list-style: none;
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          grid-gap: 8px;
          grid-row-gap: calc(8px * ${hydratedRowGap.xs});
          align-items: ${vAlign};
        }
        @media (min-width: ${mq.md}) {
          .grid {
            margin: 0 0 calc(8px * ${hydratedMargin.md});
            grid-row-gap: calc(8px * ${hydratedRowGap.md});
          }
        }
        @media (min-width: ${mq.lg}) {
          .grid {
            margin: 0 0 calc(8px * ${hydratedMargin.lg});
            grid-row-gap: calc(8px * ${hydratedRowGap.lg});
          }
        }
        @media (min-width: ${mq.xl}) {
          .grid {
            margin: 0 0 calc(8px * ${hydratedMargin.xl});
            grid-row-gap: calc(8px * ${hydratedRowGap.xl});
          }
        }
      `}</style>
    </CustomTag>
  );
};

Grid.defaultProps = {
  tag: 'div', // component tag
  rowGap: {
    // adds gap between rows
    xl: 1,
    lg: 1,
    md: 1,
    xs: 1
  },
  margin: {
    // adds component margin
    xl: 1,
    lg: 1,
    md: 1,
    xs: 1
  },
  vAlign: 'flex-start' // align of the grid
};

const GridRepeat = ({ children, tag, columns, xl, lg, md, rowGap, justify }) => {
  const CustomTag = tag;
  const hydratedRowGap = { ...GridRepeat.defaultProps.rowGap, ...rowGap };
  const hydratedColumns = { ...GridRepeat.defaultProps.columns, ...columns };
  return (
    <CustomTag className={cn('grid-repeat', { justify })}>
      {children}
      <style jsx>{`
        .grid-repeat {
          margin: 0;
          padding: 0;
          list-style: none;
          display: grid;
          grid-template-columns: 1fr;
          grid-row-gap: calc(8px * ${hydratedRowGap.xs});
        }
        @media (min-width: ${mq.md}) {
          .grid-repeat {
            grid-column-gap: 8px;
            grid-row-gap: calc(8px * ${hydratedRowGap.md});
          }
        }
        @media (min-width: ${mq.lg}) {
          .grid-repeat {
            grid-row-gap: calc(8px * ${hydratedRowGap.lg});
          }
        }
        @media (min-width: ${mq.xl}) {
          .grid-repeat {
            grid-row-gap: calc(8px * ${hydratedRowGap.xl});
          }
        }
        .grid-repeat.justify {
          justify-content: space-between;
        }
      `}</style>
    </CustomTag>
  );
};

GridRepeat.defaultProps = {
  tag: 'div', // component tag
  columns: {
    // select how many columns each viewport has
    xl: 2,
    lg: 2,
    md: 2
  },
  rowGap: {
    // adds gap between rows
    xl: 1,
    lg: 1,
    md: 1,
    xs: 1
  },
  justify: false // justify-content between columns
};

const GridItem = ({ children, tag, xl, lg, md, xs, empty, row, order, full, fullDesktop }) => {
  const CustomTag = tag;
  const hydratedRow = { ...GridItem.defaultProps.row, ...row };
  const hydratedOrder = { ...GridItem.defaultProps.order, ...order };
  return (
    <CustomTag className={cn('grid-item', { config: !empty, full, fullDesktop })}>
      {children}
      <style jsx>{`
        .grid-item.config {
          grid-column: ${xs};
          grid-row: ${hydratedRow.xs};
          order: ${hydratedOrder.xs};
        }
        @media (min-width: ${mq.md}) {
          .grid-item.config {
            grid-column: ${md};
            grid-row: ${hydratedRow.md};
            order: ${hydratedOrder.md};
          }
        }
        @media (min-width: ${mq.lg}) {
          .grid-item.config {
            grid-column: ${lg};
            grid-row: ${hydratedRow.lg};
            order: ${hydratedOrder.lg};
          }
        }
        @media (min-width: 1400px) {
          .grid-item.config {
            grid-column: ${xl};
            grid-row: ${hydratedRow.xl};
            order: ${hydratedOrder.xl};
          }
        }
        .grid-item.full {
          height: 100%;
        }
        @media (min-width: ${mq.lg}) {
          .grid-item.fullDesktop {
            height: 100%;
          }
        }
      `}</style>
    </CustomTag>
  );
};

GridItem.defaultProps = {
  tag: 'div', // component tag
  xl: '1 / 13', // select which columns the item spans
  lg: '1 / 13',
  md: '1 / 9',
  xs: '1 / 3',
  empty: false,
  full: false, // manages the height of the item
  fullDesktop: false, // manages the height of the item just for desktop
  row: {
    // select to which row the grid items belongs
    xl: 'initial',
    lg: 'initial',
    md: 'initial',
    xs: 'initial'
  },
  order: {
    // select the order of the grid item
    xl: 'initial',
    lg: 'initial',
    md: 'initial',
    xs: 'initial'
  }
};

export { Grid, GridRepeat, GridItem };
